import { useCallback, useEffect, useState } from 'react';

// „Wer war die letzte Person?" — der Wiedereinstieg nach einem Telefonat.
//
// Destiny, 17.08.2026: „Bei der Wiedervorlage startet man nach einem Telefonat
// von vorne. Das bedeutet, dass man häufig vergisst, um welchen Kunden es sich
// handelte. Daher wäre es eine Hilfe, wenn man eine Umrandung, eine kleine
// Notiz oder Ähnliches hätte, um zu prüfen, wer die letzte angerufene oder
// angeklickte Person war."
//
// Gemerkt wird genau eine Person — die zuletzt angerufene ODER geöffnete. Im
// Browser-Speicher, weil es eine Arbeitshaltung ist und kein Datensatz: es soll
// ohne Ladezeit da sein und pro Gerät gelten. Über `storage` und ein eigenes
// Ereignis erfahren alle offenen Listen und Tabs davon.

const SCHLUESSEL = 'cc-zuletzt:v1';
const EREIGNIS = 'cc-zuletzt-geaendert';

export interface ZuletztBearbeitet {
  customerId: string;
  name: string;
  /** ms seit 1970 — für „vor 3 Minuten". */
  at: number;
  /** 'anruf' = gewählt, 'geoeffnet' = Zeile angeklickt. */
  art: 'anruf' | 'geoeffnet';
}

function lesen(): ZuletztBearbeitet | null {
  try {
    const roh = localStorage.getItem(SCHLUESSEL);
    if (!roh) return null;
    const w = JSON.parse(roh) as Partial<ZuletztBearbeitet>;
    if (!w || typeof w.customerId !== 'string' || typeof w.at !== 'number') return null;
    return {
      customerId: w.customerId,
      name: typeof w.name === 'string' ? w.name : '—',
      at: w.at,
      art: w.art === 'anruf' ? 'anruf' : 'geoeffnet',
    };
  } catch {
    return null;
  }
}

/**
 * Person als „zuletzt bearbeitet" merken.
 *
 * Ein Anruf gewinnt gegen ein bloßes Öffnen: wer während des Gesprächs im
 * Profil herumklickt, soll den Gesprächspartner danach nicht durch die eigene
 * Navigation ersetzt haben. Nach fünf Minuten führt wieder die neuere Angabe.
 */
export function merkeZuletzt(
  customerId: string | null | undefined,
  name: string,
  art: ZuletztBearbeitet['art'] = 'geoeffnet',
): void {
  if (!customerId || customerId.startsWith('adhoc-')) return;
  try {
    const vorher = lesen();
    if (
      art === 'geoeffnet' && vorher?.art === 'anruf'
      && vorher.customerId !== customerId
      && Date.now() - vorher.at < 5 * 60_000
    ) return;
    const stand: ZuletztBearbeitet = { customerId, name: name || '—', at: Date.now(), art };
    localStorage.setItem(SCHLUESSEL, JSON.stringify(stand));
    window.dispatchEvent(new CustomEvent(EREIGNIS));
  } catch { /* kein Speicher (Privatmodus): dann eben ohne Merker */ }
}

/** Aktueller Merker, aktualisiert sich auch bei Änderungen in anderen Tabs. */
export function useZuletztBearbeitet(): ZuletztBearbeitet | null {
  const [stand, setStand] = useState<ZuletztBearbeitet | null>(() => lesen());
  const neuLesen = useCallback(() => setStand(lesen()), []);
  useEffect(() => {
    window.addEventListener(EREIGNIS, neuLesen);
    window.addEventListener('storage', neuLesen);
    return () => {
      window.removeEventListener(EREIGNIS, neuLesen);
      window.removeEventListener('storage', neuLesen);
    };
  }, [neuLesen]);
  return stand;
}

/** „vor 3 Minuten" / „gerade eben" — für den Hinweis über der Liste. */
export function seitText(at: number, jetzt = Date.now()): string {
  const min = Math.floor((jetzt - at) / 60_000);
  if (min < 1) return 'gerade eben';
  if (min === 1) return 'vor 1 Minute';
  if (min < 60) return `vor ${min} Minuten`;
  const std = Math.floor(min / 60);
  if (std === 1) return 'vor 1 Stunde';
  if (std < 24) return `vor ${std} Stunden`;
  const tage = Math.floor(std / 24);
  return tage === 1 ? 'gestern' : `vor ${tage} Tagen`;
}
